import { useState, useEffect } from "react";
import { Language, usePermission } from "imports/Utils";
import { useStoreState } from "ezStore";
import { InputAdornment, TextField } from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";
import { gql } from "__generated__/gql";
import { useMutation } from "@apollo/client";
import { intl } from "LocaleWrapper";
import { FormGroup, showSnackbar, Button } from "@cofano/cck";

const Revenue = ({ charter, refetch }) => {

	const { isAdmin, isFinanceAdmin } = usePermission();

	const { locale } = useStoreState(store => store.session);
	const [ revenueCorrection, setRevenueCorrection ] = useState(charter.declarations?.[0]?.correction ?? 0);

	const [ saveCorrection, { loading: declareLoading } ] = useMutation(SAVE_CORRECTION_MUTATION, {
		onCompleted: () => {
			refetch();
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
		},
		variables: {
			correction: revenueCorrection,
			id: charter.declarations?.[0]?.id,
		},
	});

	const formatPrice = (price) => {
		return new Intl.NumberFormat(locale, { minimumFractionDigits: 2 }).format(price || 0);
	};

	const calculateValues = () => {
		let weeklyCosts = 0;
		let symbol;
		let isoCode;
		if (charter.declarations?.[0]) {
			symbol = charter.declarations[0].invoiceLines[0]?.price?.currency?.symbol;
			isoCode = charter.declarations[0].invoiceLines[0]?.price?.currency?.isoCode;
			weeklyCosts = Number(charter.declarations[0].invoiceLines
				.filter(i => !i.unexpected)
				.reduce((sum, line) => sum + (line?.price.value * line?.quantity), 0)
				.toFixed(2));
		}
		return { symbol, isoCode, weeklyCosts };
	};
	const { symbol, weeklyCosts } = calculateValues();

	const didCorrectionChange = charter.declarations?.[0]?.correction !== revenueCorrection;
	const canDoCorrection = (isAdmin || isFinanceAdmin) && charter.declarations?.[0];
	const cargoRevenue = charter.voyages?.reduce((sum, voyage) => sum + voyage.cargoRevenue, 0) || 0;
	const voyageRevenue = charter.voyages?.reduce((sum, voyage) => sum + voyage.voyageRevenue, 0) || 0;
	const revenue = cargoRevenue + voyageRevenue;

	useEffect(() => {
		setRevenueCorrection(charter.declarations?.[0]?.correction ?? 0);
	}, [ charter.declarations?.[0]?.id ]);

	return (
		<div className="summary">
			<div className="labels">
				<FormGroup
					label={Language("CARGO_REVENUE")}
					labelPlacement="left-center"
					labelWidth={200}
					overflow="hidden"
				/>
				<FormGroup
					label={Language("VOYAGE_REVENUE")}
					labelPlacement="left-center"
					labelWidth={200}
					overflow="hidden"
				/>
				<FormGroup
					label={Language("REVENUE_CORRECTION")}
					labelPlacement="left-center"
					labelWidth={200}
					overflow="hidden"
				/>
				{didCorrectionChange && <span style={{ marginBottom: 28 }} />}
				<span>
					<FormGroup
						label={Language("TOTAL_REVENUE")}
						labelPlacement="left-center"
						labelWidth={200}
						overflow="hidden"
					/>
				</span>
				<span>
					<FormGroup
						label={Language("TOTAL_COSTS")}
						labelPlacement="left-center"
						labelWidth={200}
						overflow="hidden"
					/>
				</span>
				<FormGroup
					label={Language("RESULT")}
					labelPlacement="left-center"
					labelWidth={200}
					overflow="hidden"
				/>
			</div>
			<div className="numbers">
				<span>
					<TextField
						disabled
						value={formatPrice(cargoRevenue)}
						variant='standard'
						InputProps={{
							startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
							disableUnderline: true,
						}}
					/>
				</span>
				<span>
					<TextField
						disabled
						value={formatPrice(voyageRevenue)}
						variant='standard'
						InputProps={{
							startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
							disableUnderline: true,
						}}
					/>
				</span>
				<span className={canDoCorrection ? "bg-input" : ""} style={{ margin: 0 }}>
					<TextField
						disabled={!canDoCorrection}
						type="number"
						value={revenueCorrection}
						onChange={e => setRevenueCorrection(Number(e.target.value))}
						variant='standard'
						InputProps={{
							startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
							endAdornment: canDoCorrection ? <InputAdornment position="end"><CreateIcon
								color="disabled"
								style={{ fontSize: 15 }} /></InputAdornment> : "",
						}}
					/>
				</span>
				{didCorrectionChange && (
					<Button
						disabled={!didCorrectionChange}
						loading={declareLoading}
						onClick={() => saveCorrection()}
						className={didCorrectionChange ? "changed" : ""}
						size="small">
						Save correction
					</Button>
				)}
				<span>
					<TextField
						disabled
						value={formatPrice(revenue + (revenueCorrection || 0))}
						variant='standard'
						InputProps={{
							startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
							disableUnderline: true,
						}}
					/>
				</span>
				<span>
					<TextField
						disabled
						value={formatPrice(weeklyCosts)}
						variant='standard'
						InputProps={{
							startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
							disableUnderline: true,
						}}
					/>
				</span>

				<span className="borderTop">
					<TextField
						disabled
						value={formatPrice((revenue + revenueCorrection) - (weeklyCosts))}
						variant='standard'
						InputProps={{
							startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
							disableUnderline: true,
						}}
					/>
				</span>
			</div>
		</div>
	);
};

const SAVE_CORRECTION_MUTATION = gql(`#graphql
	mutation saveCorrection($id: Long!, $correction: BigDecimal) {
		saveCorrection(id: $id, correction: $correction) {
			id
		}
	}
`);

export default Revenue;
